/**
 * HowTo Schema JSON-LD Generator
 *
 * Automatically detects "How-To" style blog posts and generates valid Schema.org
 * HowTo structured data based on their markdown content.
 *
 * Detection Rules:
 * - Slug starts with "how-to"
 * - Title begins with "How to" (any language: "How to", "Wie man", "Como", "Bagaimana")
 * - Category is "guides" or "tutorials"
 *
 * Critical SEO Requirements:
 * - Follow Schema.org HowTo specification
 * - Extract steps from markdown lists or imperative sentences
 * - All text must be plain text (NO HTML)
 * - Must use localized metadata
 */

import { getCanonicalUrl } from './canonicalUrl';
import { BlogPost } from '../blog/types';

export interface HowToStep {
  '@type': 'HowToStep';
  position: number;
  name: string;
  text: string;
}

export interface HowToSchemaData {
  name: string;
  description: string;
  image?: string;
  totalTime?: string;
  step: HowToStep[];
  locale: string;
  currentPath: string;
}

/**
 * Detect if a blog post qualifies as a How-To post
 */
export function isHowToPost(post: BlogPost): boolean {
  // Check if slug starts with "how-to"
  if (post.slug.startsWith('how-to')) {
    return true;
  }

  // Check if title begins with "How to" in any language
  const howToPatterns = [
    /^how\s+to\s+/i,           // English: "How to"
    /^wie\s+man\s+/i,          // German: "Wie man"
    /^como\s+/i,               // Portuguese/Spanish: "Como"
    /^bagaimana\s+/i,          // Malay: "Bagaimana"
  ];

  if (howToPatterns.some(pattern => pattern.test(post.title))) {
    return true;
  }

  // Check if category is "guides" or "tutorials"
  if (post.category === 'guides' || post.category === 'tutorials') {
    return true;
  }

  return false;
}

/**
 * Strip markdown formatting from text
 */
function stripMarkdown(text: string): string {
  return text
    // Remove links but keep text: [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove bold/italic: **text** or *text* -> text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    // Remove inline code: `code` -> code
    .replace(/`([^`]+)`/g, '$1')
    // Remove headings: ### text -> text
    .replace(/^#{1,6}\s+/gm, '')
    // Remove blockquotes: > text -> text
    .replace(/^>\s+/gm, '')
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract How-To steps from markdown content
 */
export function extractStepsFromMarkdown(content: string): HowToStep[] {
  const steps: HowToStep[] = [];

  // Strategy 1: Look for ordered lists (1. Step, 2. Step, etc.)
  const orderedListPattern = /^\d+\.\s+(.+)$/gm;
  let matches = Array.from(content.matchAll(orderedListPattern));

  if (matches.length >= 3) {
    // Found an ordered list with at least 3 items
    return matches.map((match, index) => ({
      '@type': 'HowToStep' as const,
      position: index + 1,
      name: stripMarkdown(match[1]),
      text: stripMarkdown(match[1]),
    }));
  }

  // Strategy 2: Look for "Step X:" patterns
  const stepPatternRegex = /(?:^|\n)\*?\*?Step\s+(\d+)\*?\*?[:\s]+(.+?)(?=\n\n|\n\*?\*?Step\s+\d+|$)/gis;
  matches = Array.from(content.matchAll(stepPatternRegex));

  if (matches.length >= 3) {
    return matches.map((match) => {
      // Clean up the matched text to remove any leading ** or trailing **
      let textContent = match[2].trim();
      textContent = textContent.replace(/^\*\*\s*/, '').replace(/\s*\*\*$/, '');

      return {
        '@type': 'HowToStep' as const,
        position: parseInt(match[1]),
        name: stripMarkdown(textContent.split('\n')[0]), // First line as name
        text: stripMarkdown(textContent),
      };
    });
  }

  // Strategy 3: Look for unordered lists with step-like content
  const unorderedListPattern = /^[-*]\s+(.+)$/gm;
  matches = Array.from(content.matchAll(unorderedListPattern));

  if (matches.length >= 3) {
    // Filter for step-like content (contains imperative verbs or instructions)
    const imperativePatterns = [
      /^(open|click|press|select|copy|paste|download|upload|enter|type|go to|visit|use|install|remove|delete|create|add|edit|check|enable|disable)/i
    ];

    const stepLikeItems = matches.filter(match =>
      imperativePatterns.some(pattern => pattern.test(match[1]))
    );

    if (stepLikeItems.length >= 3) {
      return stepLikeItems.slice(0, 10).map((match, index) => ({
        '@type': 'HowToStep' as const,
        position: index + 1,
        name: stripMarkdown(match[1]),
        text: stripMarkdown(match[1]),
      }));
    }
  }

  // Strategy 4: Look for ### Step-by-Step Instructions section
  const stepByStepSection = content.match(/###\s+Step-by-Step\s+Instructions?:?\s*\n([\s\S]+?)(?=\n##|$)/i);
  if (stepByStepSection) {
    const sectionContent = stepByStepSection[1];
    const stepMatches = Array.from(sectionContent.matchAll(/^\*\*Step\s+\d+:\*\*\s+(.+)$/gm));

    if (stepMatches.length >= 3) {
      return stepMatches.map((match, index) => ({
        '@type': 'HowToStep' as const,
        position: index + 1,
        name: stripMarkdown(match[1]),
        text: stripMarkdown(match[1]),
      }));
    }
  }

  // Strategy 5: Fallback - extract sentences beginning with imperative verbs
  const imperativeSentences = content.match(/(?:^|\n)([A-Z][^.!?]*(?:Open|Click|Press|Select|Copy|Paste|Download|Upload|Enter|Type|Go to|Visit|Use|Install|Remove|Delete|Create|Add|Edit|Check|Enable|Disable)[^.!?]*[.!?])/gm);

  if (imperativeSentences && imperativeSentences.length >= 3) {
    return imperativeSentences.slice(0, 10).map((sentence, index) => ({
      '@type': 'HowToStep' as const,
      position: index + 1,
      name: stripMarkdown(sentence.trim().substring(0, 100)), // Limit name length
      text: stripMarkdown(sentence.trim()),
    }));
  }

  return steps;
}

/**
 * Estimate total time for the how-to (in ISO 8601 duration format)
 * Based on number of steps and content complexity
 */
function estimateTotalTime(steps: HowToStep[], content: string): string {
  // Base time: 1 minute per step
  let minutes = steps.length;

  // Add time based on content length (rough estimate: 200 words per minute)
  const wordCount = content.split(/\s+/).length;
  const readingMinutes = Math.ceil(wordCount / 200);

  // Total time is reading time + execution time
  minutes += readingMinutes;

  // Cap at reasonable limits
  if (minutes < 5) minutes = 5;
  if (minutes > 60) minutes = 60;

  // Return in ISO 8601 duration format: PT5M (5 minutes), PT1H (1 hour)
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
      return `PT${hours}H${remainingMinutes}M`;
    }
    return `PT${hours}H`;
  }

  return `PT${minutes}M`;
}

/**
 * Generate HowTo Schema for a blog post
 */
export function generateHowToSchema(
  post: BlogPost,
  locale: string,
  currentPath: string
): Record<string, any> | null {
  // Only generate schema for How-To posts
  if (!isHowToPost(post)) {
    return null;
  }

  // Extract steps from markdown content
  const steps = extractStepsFromMarkdown(post.content);

  // Need at least 2 steps for valid HowTo schema
  if (steps.length < 2) {
    console.warn(`[HowTo Schema] Not enough steps found for post: ${post.slug}`);
    return null;
  }

  // Get the correct canonical URL for this page (includes locale prefix)
  const canonicalUrl = getCanonicalUrl(currentPath);

  // Handle image URL - must be absolute
  const imageUrl = post.coverImage
    ? post.coverImage.startsWith('http')
      ? post.coverImage
      : `https://gpt-watermark-remover.com${post.coverImage}`
    : undefined;

  // Estimate total time
  const totalTime = estimateTotalTime(steps, post.content);

  // Build HowTo schema
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: post.title,
    description: post.description,
    totalTime: totalTime,
    step: steps,
    publisher: {
      '@type': 'Organization',
      name: 'GROW FAST LTD.',
      url: 'https://gpt-watermark-remover.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  // Add image if available
  if (imageUrl) {
    schema.image = imageUrl;
  }

  return schema;
}

/**
 * Inject HowTo JSON-LD script into document head
 * Safe for both SSR and client-side rendering
 * Prevents duplicate injection
 */
export function injectHowToSchema(schema: Record<string, any>): () => void {
  // Guard against SSR
  if (typeof window === 'undefined') {
    return () => {}; // No-op cleanup function
  }

  // Check if HowTo schema already exists
  const existingHowToSchema = document.querySelector('script[type="application/ld+json"][data-schema-type="HowTo"]');
  if (existingHowToSchema) {
    console.log('[HowTo Schema] Schema already exists, skipping duplicate injection');
    return () => {
      if (document.head.contains(existingHowToSchema)) {
        document.head.removeChild(existingHowToSchema);
      }
    };
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema-type', 'HowTo');
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);

  console.log('[HowTo Schema] Injected HowTo schema for:', schema.name);

  // Return cleanup function
  return () => {
    if (document.head.contains(script)) {
      document.head.removeChild(script);
    }
  };
}
